import { Notifier } from '@/lib/notifier'
import type { DirectionFor, BottomSheetView } from '@/types/enums'
import type { DirectionModel, LatLng } from '@/types/structs'
import { TEST_POSITION } from '@/config/constants'

/* Port of FFAppState (app_state.dart) — the app-wide singleton store.
 *
 * THE core parity rule (spec §9.1): every Dart setter is a SILENT plain
 * assignment; only `update(cb)` calls notifyListeners(). All 14 live call
 * sites pass an empty callback — `update()` is a pure "publish". In this
 * port, direct field assignment IS the silent setter; call
 * `appState.update()` to publish.
 *
 * Four live spots mutate WITHOUT publishing ("see history", history "Close",
 * the /directions pills, loadDistanceMarkerDescriptor's in-place distance
 * mutation) — the map keeps seeing the stale value until someone publishes.
 * That staleness is load-bearing; do not add reactivity here.
 *
 * Not ported (dead in the original):
 *  - initializePersistedState(): empty body — nothing persists, every reload
 *    resets to defaults;
 *  - updateWalking/Bicycling/DrivingDirectionModelStruct: never called;
 *  - static reset(): never called, and broken anyway (the Provider closure
 *    captured the original instance). */

class FFAppState extends Notifier {
  /** Default DirectionFor.walk. */
  selectedDirectionFor: DirectionFor | null = 'walk'

  /** (sic — original typo). Copenhagen. Written by NOBODY; read in debug
   * builds as the mock current location. */
  testPossition: LatLng | null = { ...TEST_POSITION }

  currentBottomSheetView: BottomSheetView | null = 'initial'

  /** Empty structs: dirDistance(model).value === 0 -> the < 20000 walk gate
   * is TRUE for an empty model. */
  walkingDirectionModel: DirectionModel = {}
  bicyclingDirectionModel: DirectionModel = {}
  drivingDirectionModel: DirectionModel = {}

  /** Written only by startGetingCurrentUserLocation (once + every 10 s). */
  myPosition: LatLng | null = null

  sendReassuranceAnimation = false

  /** `void update(VoidCallback callback) { callback(); notifyListeners(); }` */
  update(callback: () => void = () => {}): void {
    callback()
    this.notifyListeners()
  }
}

export const appState = new FFAppState()
